import {
  Button,
  Card,
  CardContent,
  ConfirmSheet,
  FormField,
  Input,
  PageHeader,
  SelectField,
  SwitchField,
} from "@jod/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Copy, Monitor, RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";
import { useOwnerState } from "../../features/backoffice/queries.ts";
import {
  type ModerationMode,
  parseBahtInput,
  satangToBaht,
  screenApi,
  type TipTarget,
  type UpdateScreenSettingsInput,
  type UpsertTipTargetInput,
} from "../../features/screen/screen-api.ts";

export const Route = createFileRoute("/_app/screen")({ component: VenueScreenSettings });

const SCREEN_BASE_URL: string =
  (import.meta.env.VITE_VENUE_SCREEN_BASE_URL as string | undefined) ?? "http://localhost:5276";

const SCREEN_QUERY_KEY = (venueId: string) => ["screen", "settings", venueId] as const;
const TIP_TARGETS_QUERY_KEY = (venueId: string) => ["screen", "tip-targets", venueId] as const;

function useScreenSettings(venueId: string) {
  return useQuery({
    queryKey: SCREEN_QUERY_KEY(venueId),
    queryFn: () => {
      if (!screenApi) throw new Error("Screen API unavailable in mock mode");
      return screenApi.getSettings(venueId);
    },
    enabled: Boolean(venueId),
  });
}

function useTipTargets(venueId: string) {
  return useQuery({
    queryKey: TIP_TARGETS_QUERY_KEY(venueId),
    queryFn: () => {
      if (!screenApi) throw new Error("Screen API unavailable in mock mode");
      return screenApi.getTipTargets(venueId);
    },
    enabled: Boolean(venueId),
  });
}

function useUpdateScreenSettings(venueId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateScreenSettingsInput) => {
      if (!screenApi) throw new Error("Screen API unavailable in mock mode");
      return screenApi.updateSettings(input);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SCREEN_QUERY_KEY(venueId) });
    },
  });
}

function useRotateToken(venueId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!screenApi) throw new Error("Screen API unavailable in mock mode");
      return screenApi.rotateToken(venueId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SCREEN_QUERY_KEY(venueId) });
    },
  });
}

function useUpsertTipTarget(venueId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertTipTargetInput) => {
      if (!screenApi) throw new Error("Screen API unavailable in mock mode");
      return screenApi.upsertTipTarget(input);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TIP_TARGETS_QUERY_KEY(venueId) });
    },
  });
}

// ---- Sub-components ----

interface PriceFieldProps {
  id: string;
  label: string;
  valueSatang: number;
  onChange: (satang: number | null) => void;
  error?: string;
}

function PriceField({ id, label, valueSatang, onChange, error }: PriceFieldProps) {
  const [raw, setRaw] = useState(String(satangToBaht(valueSatang)));

  useEffect(() => {
    setRaw(String(satangToBaht(valueSatang)));
  }, [valueSatang]);

  return (
    <FormField
      id={id}
      label={`${label} (฿)`}
      hint={error ? <span className="text-destructive">{error}</span> : undefined}
    >
      <Input
        id={id}
        type="number"
        min={1}
        step={1}
        value={raw}
        onChange={(e) => {
          setRaw(e.target.value);
          onChange(parseBahtInput(e.target.value));
        }}
      />
    </FormField>
  );
}

interface TipTargetRowProps {
  target: TipTarget;
  venueId: string;
  onUpsert: ReturnType<typeof useUpsertTipTarget>["mutate"];
  isPending: boolean;
}

function TipTargetRow({ target, venueId, onUpsert, isPending }: TipTargetRowProps) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(target.label);
  const [sortOrder, setSortOrder] = useState(String(target.sortOrder));

  const save = () => {
    const order = Number(sortOrder);
    if (!label.trim() || label.length > 80 || !Number.isInteger(order)) return;
    onUpsert({
      venueId,
      id: target.id,
      label: label.trim(),
      active: target.active,
      sortOrder: order,
    });
    setEditing(false);
  };

  if (editing) {
    return (
      <tr className="border-b border-border">
        <td className="py-2 pr-3">
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={80}
            aria-label="ชื่อ tip target"
          />
        </td>
        <td className="py-2 pr-3">
          <Input
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="w-20"
            aria-label="ลำดับ"
          />
        </td>
        <td className="py-2 pr-3 text-center">
          <input
            type="checkbox"
            checked={target.active}
            onChange={(e) =>
              onUpsert({
                venueId,
                id: target.id,
                label: target.label,
                active: e.target.checked,
                sortOrder: target.sortOrder,
              })
            }
            className="size-4 accent-primary"
            aria-label="เปิดใช้งาน"
          />
        </td>
        <td className="py-2">
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={isPending}>
              บันทึก
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              ยกเลิก
            </Button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b border-border">
      <td className="py-2 pr-3 text-sm">{target.label}</td>
      <td className="py-2 pr-3 text-sm text-muted-foreground">{target.sortOrder}</td>
      <td className="py-2 pr-3 text-center text-sm">
        <input
          type="checkbox"
          checked={target.active}
          onChange={(e) =>
            onUpsert({
              venueId,
              id: target.id,
              label: target.label,
              active: e.target.checked,
              sortOrder: target.sortOrder,
            })
          }
          className="size-4 accent-primary"
          aria-label="เปิดใช้งาน"
        />
      </td>
      <td className="py-2">
        <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
          แก้ไข
        </Button>
      </td>
    </tr>
  );
}

interface NewTipTargetRowProps {
  venueId: string;
  onUpsert: ReturnType<typeof useUpsertTipTarget>["mutate"];
  isPending: boolean;
  nextSortOrder: number;
}

function NewTipTargetRow({ venueId, onUpsert, isPending, nextSortOrder }: NewTipTargetRowProps) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [sortOrder, setSortOrder] = useState(String(nextSortOrder));

  if (!open) {
    return (
      <tr>
        <td colSpan={4} className="pt-3">
          <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
            + เพิ่ม tip target
          </Button>
        </td>
      </tr>
    );
  }

  const save = () => {
    const order = Number(sortOrder);
    if (!label.trim() || label.length > 80 || !Number.isInteger(order)) return;
    onUpsert({ venueId, label: label.trim(), active: true, sortOrder: order });
    setLabel("");
    setSortOrder(String(order + 1));
    setOpen(false);
  };

  return (
    <tr className="border-b border-border">
      <td className="py-2 pr-3">
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={80}
          placeholder="เช่น วงดนตรี"
          aria-label="ชื่อ tip target ใหม่"
        />
      </td>
      <td className="py-2 pr-3">
        <Input
          type="number"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          className="w-20"
          aria-label="ลำดับ"
        />
      </td>
      <td className="py-2 pr-3" />
      <td className="py-2">
        <div className="flex gap-2">
          <Button size="sm" onClick={save} disabled={isPending || !label.trim()}>
            บันทึก
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
            ยกเลิก
          </Button>
        </div>
      </td>
    </tr>
  );
}

// ---- Main page component ----

function VenueScreenSettings() {
  const { data: ownerState } = useOwnerState();
  const venueId = ownerState?.currentVenueId ?? "";

  const { data: settings, isLoading, error } = useScreenSettings(venueId);
  const { data: tipTargets } = useTipTargets(venueId);
  const updateSettings = useUpdateScreenSettings(venueId);
  const rotateToken = useRotateToken(venueId);
  const upsertTipTarget = useUpsertTipTarget(venueId);

  // Pricing draft (in baht as strings for controlled inputs)
  const [priceDraft, setPriceDraft] = useState({
    priceMessageSatang: null as number | null,
    priceWarpSatang: null as number | null,
    priceSongRequestSatang: null as number | null,
    tipMinSatang: null as number | null,
  });
  const [priceErrors, setPriceErrors] = useState<Record<string, string>>({});
  const [rotateConfirmOpen, setRotateConfirmOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Sync draft from loaded settings
  useEffect(() => {
    if (!settings) return;
    setPriceDraft({
      priceMessageSatang: settings.priceMessageSatang,
      priceWarpSatang: settings.priceWarpSatang,
      priceSongRequestSatang: settings.priceSongRequestSatang,
      tipMinSatang: settings.tipMinSatang,
    });
  }, [settings]);

  if (!venueId) {
    return <PageHeader title="จอใหญ่ (Venue Screen)" subtitle="Loading…" />;
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="จอใหญ่ (Venue Screen)"
          subtitle="ตั้งค่าฟีเจอร์ pay-to-post สำหรับจอใหญ่ในสถานที่"
        />
        <p className="text-muted-foreground text-sm">กำลังโหลด…</p>
      </div>
    );
  }

  if (error || !settings) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="จอใหญ่ (Venue Screen)"
          subtitle="ตั้งค่าฟีเจอร์ pay-to-post สำหรับจอใหญ่ในสถานที่"
        />
        <Card>
          <CardContent className="p-5">
            <p className="text-destructive text-sm">
              {error instanceof Error ? error.message : "ไม่สามารถโหลดข้อมูลได้"}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const kioskUrl = `${SCREEN_BASE_URL}/s/${settings.screenToken}`;

  const handleCopy = () => {
    void navigator.clipboard.writeText(kioskUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSavePricing = () => {
    const errors: Record<string, string> = {};
    const fields: Array<[keyof typeof priceDraft, string]> = [
      ["priceMessageSatang", "ราคาข้อความ"],
      ["priceWarpSatang", "ราคาวาร์ป"],
      ["priceSongRequestSatang", "ราคาขอเพลง"],
      ["tipMinSatang", "ทิปขั้นต่ำ"],
    ];
    for (const [key, label] of fields) {
      if (priceDraft[key] == null) {
        errors[key] = `${label}: ต้องเป็นจำนวนเต็มบวก`;
      }
    }
    setPriceErrors(errors);
    if (Object.keys(errors).length > 0) return;

    updateSettings.mutate({
      venueId,
      priceMessageSatang: priceDraft.priceMessageSatang ?? settings.priceMessageSatang,
      priceWarpSatang: priceDraft.priceWarpSatang ?? settings.priceWarpSatang,
      priceSongRequestSatang: priceDraft.priceSongRequestSatang ?? settings.priceSongRequestSatang,
      tipMinSatang: priceDraft.tipMinSatang ?? settings.tipMinSatang,
    });
  };

  const nextSortOrder = tipTargets ? Math.max(0, ...tipTargets.map((t) => t.sortOrder)) + 1 : 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="จอใหญ่ (Venue Screen)"
        subtitle="ตั้งค่าฟีเจอร์ pay-to-post สำหรับจอใหญ่ในสถานที่"
        actions={
          updateSettings.isPending ? (
            <span className="text-muted-foreground text-xs">กำลังบันทึก…</span>
          ) : updateSettings.isSuccess ? (
            <span className="text-xs text-green-600">บันทึกแล้ว</span>
          ) : null
        }
      />

      {/* Section A: Enable + Moderation Mode */}
      <Card>
        <CardContent className="space-y-4 p-5">
          <h2 className="font-display font-semibold text-sm">การเปิด/ปิด และโหมดตรวจสอบ</h2>
          <SwitchField
            label="เปิดใช้งานจอใหญ่"
            description="เมื่อเปิด ลูกค้าสามารถส่งข้อความขึ้นจอได้หลังชำระเงิน"
            checked={settings.enabled}
            onChange={(enabled) => updateSettings.mutate({ venueId, enabled })}
          />
          <SelectField
            id="moderationMode"
            label="โหมดตรวจสอบ"
            value={settings.moderationMode}
            options={[
              {
                value: "pre_approval",
                label: "พนักงานอนุมัติก่อนขึ้นจอ (แนะนำ)",
              },
              {
                value: "auto",
                label: "ขึ้นจอทันทีหลังยืนยันชำระเงิน",
              },
            ]}
            onChange={(value) =>
              updateSettings.mutate({ venueId, moderationMode: value as ModerationMode })
            }
            hint="pre_approval = ลดความเสี่ยงเนื้อหาไม่เหมาะสม"
          />
        </CardContent>
      </Card>

      {/* Section B: Pricing */}
      <Card>
        <CardContent className="space-y-4 p-5">
          <h2 className="font-display font-semibold text-sm">ราคา (แสดงเป็นบาท)</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <PriceField
              id="priceMessage"
              label="ราคาข้อความ"
              valueSatang={priceDraft.priceMessageSatang ?? settings.priceMessageSatang}
              onChange={(v) => setPriceDraft((d) => ({ ...d, priceMessageSatang: v }))}
              error={priceErrors.priceMessageSatang}
            />
            <PriceField
              id="priceWarp"
              label="ราคาวาร์ป"
              valueSatang={priceDraft.priceWarpSatang ?? settings.priceWarpSatang}
              onChange={(v) => setPriceDraft((d) => ({ ...d, priceWarpSatang: v }))}
              error={priceErrors.priceWarpSatang}
            />
            <PriceField
              id="priceSongRequest"
              label="ราคาขอเพลง"
              valueSatang={priceDraft.priceSongRequestSatang ?? settings.priceSongRequestSatang}
              onChange={(v) => setPriceDraft((d) => ({ ...d, priceSongRequestSatang: v }))}
              error={priceErrors.priceSongRequestSatang}
            />
            <PriceField
              id="tipMin"
              label="ทิปขั้นต่ำ"
              valueSatang={priceDraft.tipMinSatang ?? settings.tipMinSatang}
              onChange={(v) => setPriceDraft((d) => ({ ...d, tipMinSatang: v }))}
              error={priceErrors.tipMinSatang}
            />
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={handleSavePricing} disabled={updateSettings.isPending}>
              บันทึกราคา
            </Button>
            {updateSettings.isError ? (
              <span className="text-destructive text-xs">
                {updateSettings.error instanceof Error
                  ? updateSettings.error.message
                  : "เกิดข้อผิดพลาด"}
              </span>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {/* Section C: Tip Targets */}
      <Card>
        <CardContent className="p-5">
          <h2 className="font-display font-semibold mb-3 text-sm">เป้าหมายทิป</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border text-muted-foreground text-xs">
                  <th className="pb-2 pr-3 font-medium">ชื่อ</th>
                  <th className="pb-2 pr-3 font-medium">ลำดับ</th>
                  <th className="pb-2 pr-3 text-center font-medium">เปิดใช้</th>
                  <th className="pb-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {(tipTargets ?? []).map((target) => (
                  <TipTargetRow
                    key={target.id}
                    target={target}
                    venueId={venueId}
                    onUpsert={upsertTipTarget.mutate}
                    isPending={upsertTipTarget.isPending}
                  />
                ))}
                <NewTipTargetRow
                  venueId={venueId}
                  onUpsert={upsertTipTarget.mutate}
                  isPending={upsertTipTarget.isPending}
                  nextSortOrder={nextSortOrder}
                />
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Section D: Kiosk URL */}
      <Card>
        <CardContent className="space-y-3 p-5">
          <div className="flex items-center gap-2">
            <Monitor className="size-4 text-muted-foreground" />
            <h2 className="font-display font-semibold text-sm">URL สำหรับจอ (Kiosk)</h2>
          </div>
          <p className="text-muted-foreground text-xs">
            เปิด URL นี้บนทีวีหรือจอแสดงผลในสถานที่ เปลี่ยน token จะทำให้ลิงก์เดิมใช้ไม่ได้
          </p>
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2">
            <code className="flex-1 break-all text-xs">{kioskUrl}</code>
            <Button
              size="sm"
              variant="ghost"
              className="shrink-0 gap-1.5"
              onClick={handleCopy}
              aria-label="คัดลอก URL"
            >
              <Copy className="size-3.5" />
              {copied ? "คัดลอกแล้ว" : "คัดลอก"}
            </Button>
          </div>
          <div>
            <Button
              variant="secondary"
              size="sm"
              className="gap-2 text-destructive hover:text-destructive"
              onClick={() => setRotateConfirmOpen(true)}
              disabled={rotateToken.isPending}
            >
              <RotateCcw className="size-3.5" />
              Rotate token
            </Button>
          </div>
          <p className="text-muted-foreground text-xs">
            อัปเดตล่าสุด: {new Date(settings.updatedAt).toLocaleString("th-TH")}
          </p>
        </CardContent>
      </Card>

      {/* Rotate token confirmation */}
      <ConfirmSheet
        open={rotateConfirmOpen}
        onOpenChange={setRotateConfirmOpen}
        title="Rotate screen token?"
        description="ลิงก์จอ (kiosk URL) เดิมจะหยุดทำงานทันที คุณต้องอัปเดต URL บนทีวีใหม่ ยืนยันหรือไม่?"
        confirmLabel="ยืนยัน — Rotate"
        cancelLabel="ยกเลิก"
        destructive
        onConfirm={() => rotateToken.mutate()}
      />
    </div>
  );
}
